import { mapTeamResponseDto } from 'modules/teams/domain/data-mappers/response-dto';
import { GetTeamByIdResponseDto, TeamResponseDto } from 'modules/teams/domain/interfaces/response-dto';
import { Team } from 'modules/teams/domain/interfaces/team';
import { baseApi } from 'shared/api/base-api';

/** Тег перечня команд: по нему мутации сбрасывают именно список, а не карточки. */
export const TEAMS_LIST_TAG = { type: 'Team' as const, id: 'PARTIAL-LIST' };

/**
 * Всё, что нужно карточке команды, одним запросом: основные данные, форма,
 * состав с ролями и заявки на вступление. Раньше страница делала три запроса
 * к одному и тому же полю `team(id)`.
 */
const GET_TEAM_BY_ID_QUERY = `#graphql
  query getTeamById($id: Int!) {
    team(id: $id) {
      id
      name
      shortName
      createdAt
      emblem {
        id
        externalUrl
      }
      tournament {
        sportType
      }
      kits {
        id
        type
        elements {
          element
          attributes {
            pattern
            colors
          }
        }
      }
      members {
        id
        number
        position
        role
        createdAt
        user {
          id
          name
          surname
          middleName
          birthDate
          height
          weight
          isReal
          photo {
            id
            externalUrl
          }
        }
      }
      entryRequests {
        id
        status
        createdAt
        requestingUser {
          id
          name
          surname
          middleName
          birthDate
          height
          weight
          createdAt
          photo {
            id
            externalUrl
          }
        }
      }
    }
  }
`;

/**
 * Экспортируется для юнит-тестов. `team(id:)` в схеме nullable, поэтому удалённая
 * команда даёт `null`, а не исключение внутри transformResponse.
 */
export function mapGetTeamByIdResponse(raw: GetTeamByIdResponseDto): Team | null {
  return raw?.team ? mapTeamResponseDto([raw.team])[0] : null;
}

/**
 * Список команд для страницы `/teams`. Запрос повторяет
 * `teamsRepository.getTournamentTeams`, но живёт в RTK Query: пагинация страницы
 * построена на `usePagedList`, которому нужны `data`/`currentData` и инвалидация
 * по тегам, а не накопительный слайс.
 *
 * Состав тянем только идентификаторами игроков — строке списка нужен лишь счётчик.
 */
const LIST_TEAMS_QUERY = `#graphql
  query getTournamentTeams($tournamentId: Int!, $sportType: SportType!, $limit: Int, $offset: Int, $namePart: String) {
    teamsPaged(input: { tournamentId: $tournamentId, sportType: $sportType, limit: $limit, offset: $offset, namePart: $namePart }) {
      id
      name
      shortName
      emblem {
        id
        externalUrl
      }
      createdAt
      members {
        id
        user {
          id
          isReal
        }
      }
    }
  }
`;

export interface ListTeamsArgs {
  tournamentId: number;
  /** В схеме тип `SportType!` заглавными — приводим на входе в запрос. */
  sportType: string;
  limit: number;
  offset: number;
  namePart?: string;
}

/** Экспортируется для юнит-тестов: пустой ответ должен давать пустой список, а не падать. */
export function mapListTeamsResponse(raw: { teamsPaged?: TeamResponseDto[] }): Team[] {
  return mapTeamResponseDto(raw?.teamsPaged ?? []);
}

export const teamsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listTeams: build.query<Team[], ListTeamsArgs>({
      query: ({ tournamentId, sportType, limit, offset, namePart }) => ({
        query: LIST_TEAMS_QUERY,
        variables: { tournamentId, sportType: sportType.toUpperCase(), limit, offset, namePart },
      }),
      transformResponse: mapListTeamsResponse,
      // Идентификаторы команд рядом с тегом списка: правка команды (`invalidateTeam`)
      // обновляет и её строку в перечне, а создание/удаление бьёт по тегу списка.
      providesTags: (result) => [TEAMS_LIST_TAG, ...(result ?? []).map((team) => ({ type: 'Team' as const, id: team.id }))],
    }),

    getTeamById: build.query<Team | null, number>({
      query: (id) => ({ query: GET_TEAM_BY_ID_QUERY, variables: { id } }),
      transformResponse: mapGetTeamByIdResponse,
      providesTags: (_result, _error, id) => [{ type: 'Team', id }],
      keepUnusedDataFor: 120,
    }),
  }),
  overrideExisting: false,
});

export const { useListTeamsQuery, useLazyListTeamsQuery, useGetTeamByIdQuery, useLazyGetTeamByIdQuery } = teamsApi;
